import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userApi, authApi, companyApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Users, 
  Plus, 
  Trash2, 
  Key,
  Loader2,
  User,
  Shield,
  UserCog,
  Pencil,
  Eye,
  Building2,
  UserCheck,
  Link2
} from 'lucide-react';
import { toast } from 'sonner';
import { humanApi } from '../lib/api';

const PERMISSION_OPTIONS = [
  { id: 'dashboard', label: 'Dashboard', description: 'Visualizar painel principal e indicadores' },
  { id: 'daily_control', label: 'Operacional', description: 'Banco de Ideias, Humanas, Escalas, OSs, Estoque' },
  { id: 'reports', label: 'Relatórios', description: 'Performance, Folha CLT, Por Pessoa' },
  { id: 'users', label: 'Equipe', description: 'Gerenciar usuários e permissões' },
  { id: 'settings', label: 'Configurações', description: 'Feriados, Competências, Configurações' },
  { id: 'alterar_situacao_os', label: 'Pode alterar situação de OS', description: 'Permite mudar a situação (Confirmado, Cancelado, etc.) no módulo Comercial — sem ver valores' },
];

const UsersPage = () => {
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Create user dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    company_id: '',
    permissions: ['dashboard', 'daily_control']
  });
  const [creating, setCreating] = useState(false);
  
  // Reset password dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  
  // Permissions dialog
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState([]);
  const [editingRole, setEditingRole] = useState('user');
  const [editingName, setEditingName] = useState('');
  const [editingEmail, setEditingEmail] = useState('');
  const [editingVisibleForAssignment, setEditingVisibleForAssignment] = useState(true);
  const [savingPermissions, setSavingPermissions] = useState(false);

  // Vincular funcionário dialog
  const [vincularDialogOpen, setVincularDialogOpen] = useState(false);
  const [userToVincular, setUserToVincular] = useState(null);
  const [funcionarios, setFuncionarios] = useState([]);
  const [selectedFuncionarioId, setSelectedFuncionarioId] = useState('');
  const [vinculando, setVinculando] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, companiesRes] = await Promise.all([
        userApi.list(),
        isSuperAdmin() ? companyApi.list() : Promise.resolve({ data: [] }),
      ]);
      setUsers(usersRes.data);
      setCompanies(companiesRes.data);
      
      // Carregar funcionários para vinculação
      try {
        const humansRes = await humanApi.list();
        const colaboradores = (humansRes.data.humans || humansRes.data || [])
          .filter(h => h.type === 'colaborador');
        setFuncionarios(colaboradores);
      } catch (e) {
        console.error('Erro ao carregar funcionários:', e);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      toast.error(detail || `Erro ao carregar usuários${status ? ` (HTTP ${status})` : ''}`);
    } finally {
      setLoading(false);
    }
  };

  const handleVincularFuncionario = async () => {
    if (!userToVincular || !selectedFuncionarioId) {
      toast.error('Selecione um funcionário');
      return;
    }
    
    setVinculando(true);
    try {
      await userApi.vincularFuncionario(userToVincular.id, selectedFuncionarioId);
      toast.success('Usuário vinculado ao funcionário com sucesso!');
      setVincularDialogOpen(false);
      setUserToVincular(null);
      setSelectedFuncionarioId('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao vincular');
    } finally {
      setVinculando(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setCreating(true);
    try {
      const userData = { ...newUser };
      if (!isSuperAdmin()) {
        userData.company_id = user.company_id;
      }
      
      await authApi.register(userData);
      toast.success('Usuário criado com sucesso!');
      setNewUser({ 
        name: '', 
        email: '', 
        password: '', 
        role: 'user', 
        company_id: '',
        permissions: ['dashboard', 'daily_control']
      });
      setCreateDialogOpen(false);
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao criar usuário';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  // Delete user dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await userApi.delete(userToDelete.id);
      toast.success('Usuário excluído');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Erro ao excluir usuário');
    } finally {
      setDeleting(false);
    }
  };
  
  const openDeleteDialog = (u, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (u.id === user.id) {
      toast.error('Você não pode excluir seu próprio usuário');
      return;
    }
    
    setUserToDelete(u);
    setDeleteDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setResetting(true);
    try {
      await authApi.resetPassword(selectedUserId, newPassword);
      toast.success('Senha resetada com sucesso!');
      setNewPassword('');
      setResetDialogOpen(false);
    } catch (error) {
      toast.error('Erro ao resetar senha');
    } finally {
      setResetting(false);
    }
  };

  const openResetDialog = (userId) => {
    setSelectedUserId(userId);
    setNewPassword('');
    setResetDialogOpen(true);
  };
  
  const openPermissionsDialog = (targetUser) => {
    setEditingUser(targetUser);
    setEditingPermissions(targetUser.permissions || ['dashboard', 'daily_control']);
    setEditingRole(targetUser.role);
    setEditingName(targetUser.name || '');
    setEditingEmail(targetUser.email || '');
    setEditingVisibleForAssignment(targetUser.visible_for_assignment !== false); // Default to true
    setPermissionsDialogOpen(true);
  };
  
  const handleSavePermissions = async () => {
    if (!editingUser) return;
    
    if (!editingName.trim()) {
      toast.error('O nome é obrigatório');
      return;
    }
    
    if (!editingEmail.trim()) {
      toast.error('O email é obrigatório');
      return;
    }
    
    setSavingPermissions(true);
    try {
      await userApi.updatePermissions(editingUser.id, {
        permissions: editingPermissions,
        role: editingRole,
        name: editingName.trim(),
        email: editingEmail.trim(),
        visible_for_assignment: editingVisibleForAssignment
      });
      toast.success('Usuário atualizado com sucesso!');
      setPermissionsDialogOpen(false);
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao atualizar usuário';
      toast.error(message);
    } finally {
      setSavingPermissions(false);
    }
  };
  
  const togglePermission = (permissionId, isCreating = false) => {
    if (isCreating) {
      setNewUser(prev => ({
        ...prev,
        permissions: prev.permissions.includes(permissionId)
          ? prev.permissions.filter(p => p !== permissionId)
          : [...prev.permissions, permissionId]
      }));
    } else {
      setEditingPermissions(prev => 
        prev.includes(permissionId)
          ? prev.filter(p => p !== permissionId)
          : [...prev, permissionId]
      );
    }
  };
  
  const handleRoleChange = (role, isCreating = false) => {
    if (isCreating) {
      // Definir permissões padrão baseado no cargo
      let defaultPermissions = [];
      if (role === 'admin' || role === 'super_admin') {
        defaultPermissions = ['dashboard', 'daily_control', 'reports', 'users', 'settings'];
      } else if (role === 'viewer') {
        defaultPermissions = ['dashboard'];
      } else {
        defaultPermissions = ['dashboard', 'daily_control'];
      }
      setNewUser(prev => ({ ...prev, role, permissions: defaultPermissions }));
    } else {
      // Definir permissões padrão baseado no cargo
      if (role === 'admin' || role === 'super_admin') {
        setEditingPermissions(['dashboard', 'daily_control', 'reports', 'users', 'settings']);
      } else if (role === 'viewer') {
        setEditingPermissions(['dashboard']);
      } else {
        setEditingPermissions(['dashboard', 'daily_control']);
      }
      setEditingRole(role);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin':
        return <Shield className="w-4 h-4 text-blue-500" />;
      case 'admin':
        return <UserCog className="w-4 h-4 text-blue-500" />;
      case 'diretoria':
        return <Building2 className="w-4 h-4 text-cyan-500" />;
      case 'viewer':
        return <Eye className="w-4 h-4 text-amber-500" />;
      default:
        return <User className="w-4 h-4 text-slate-400" />;
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Admin';
      case 'diretoria':
        return 'Diretoria';
      case 'viewer':
        return 'Visualizador';
      default:
        return 'Usuário';
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || 'N/A';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="users-page" className="space-y-6 animate-fade-in">
      <Card className="dark:bg-slate-800/50 dark:border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 dark:text-slate-100">
              <Users className="w-5 h-5" />
              Equipe
            </CardTitle>
            <CardDescription className="dark:text-slate-400">
              Gerencie os usuários e suas permissões de acesso
            </CardDescription>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-button" className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Novo Usuário
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg dark:bg-slate-800 dark:border-slate-700">
              <DialogHeader>
                <DialogTitle className="dark:text-slate-100">Criar Usuário</DialogTitle>
                <DialogDescription className="dark:text-slate-400">
                  Adicione um novo usuário e defina suas permissões de acesso.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-2">
                  <Label htmlFor="user-name" className="dark:text-slate-300">Nome</Label>
                  <Input
                    id="user-name"
                    data-testid="user-name-input"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    placeholder="Nome completo"
                    className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-email" className="dark:text-slate-300">Email</Label>
                  <Input
                    id="user-email"
                    type="email"
                    data-testid="user-email-input"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="email@exemplo.com"
                    className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-password" className="dark:text-slate-300">Senha Inicial</Label>
                  <Input
                    id="user-password"
                    type="password"
                    data-testid="user-password-input"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="Mínimo 6 caracteres"
                    className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-role" className="dark:text-slate-300">Tipo de Acesso</Label>
                  <Select
                    value={newUser.role}
                    onValueChange={(value) => handleRoleChange(value, true)}
                  >
                    <SelectTrigger data-testid="user-role-select" className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                      <SelectItem value="viewer" className="dark:text-slate-200 dark:focus:bg-slate-700">
                        <div className="flex items-center gap-2">
                          <Eye className="w-4 h-4 text-amber-500" />
                          <span>Visualizador - Só vê tarefas atribuídas</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="user" className="dark:text-slate-200 dark:focus:bg-slate-700">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-500" />
                          <span>Usuário - Acesso padrão</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="diretoria" className="dark:text-slate-200 dark:focus:bg-slate-700">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-cyan-500" />
                          <span>Diretoria - Vê departamentos e setores</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="admin" className="dark:text-slate-200 dark:focus:bg-slate-700">
                        <div className="flex items-center gap-2">
                          <UserCog className="w-4 h-4 text-blue-500" />
                          <span>Admin - Acesso total</span>
                        </div>
                      </SelectItem>
                      {isSuperAdmin() && (
                        <SelectItem value="super_admin" className="dark:text-slate-200 dark:focus:bg-slate-700">
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-blue-500" />
                            <span>Super Admin</span>
                          </div>
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                {newUser.role !== 'admin' && newUser.role !== 'super_admin' && (
                  <div className="space-y-3 pt-2 border-t dark:border-slate-700">
                    <Label className="dark:text-slate-300">Permissões de Acesso</Label>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Selecione quais módulos este usuário pode acessar</p>
                    <div className="space-y-2">
                      {PERMISSION_OPTIONS.map((perm) => (
                        <div 
                          key={perm.id} 
                          className="flex items-center space-x-3 p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700"
                        >
                          <Checkbox
                            id={`create-perm-${perm.id}`}
                            checked={newUser.permissions.includes(perm.id)}
                            onCheckedChange={() => togglePermission(perm.id, true)}
                          />
                          <div className="flex-1">
                            <label 
                              htmlFor={`create-perm-${perm.id}`}
                              className="text-sm font-medium cursor-pointer dark:text-slate-200"
                            >
                              {perm.label}
                            </label>
                            <p className="text-xs text-slate-500">{perm.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {isSuperAdmin() && (
                  <div className="space-y-2">
                    <Label htmlFor="user-company">Empresa</Label>
                    <Select
                      value={newUser.company_id}
                      onValueChange={(value) => setNewUser({ ...newUser, company_id: value })}
                    >
                      <SelectTrigger data-testid="user-company-select">
                        <SelectValue placeholder="Selecione a empresa" />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                        {companies.map((company) => (
                          <SelectItem key={company.id} value={company.id} className="dark:text-slate-200 dark:focus:bg-slate-700">
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="dark:border-slate-600 dark:text-slate-300">
                  Cancelar
                </Button>
                <Button
                  data-testid="create-user-submit"
                  onClick={handleCreateUser}
                  disabled={creating}
                >
                  {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Criar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="dark:border-slate-700">
                <TableHead className="dark:text-slate-300">Usuário</TableHead>
                <TableHead className="dark:text-slate-300">Email</TableHead>
                <TableHead className="dark:text-slate-300">Tipo</TableHead>
                {isSuperAdmin() && <TableHead className="dark:text-slate-300">Empresa</TableHead>}
                <TableHead className="dark:text-slate-300">Última Atividade</TableHead>
                <TableHead className="text-right dark:text-slate-300">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isSuperAdmin() ? 6 : 5} className="text-center text-slate-500 dark:text-slate-400">
                    Nenhum usuário cadastrado
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="dark:border-slate-700">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getRoleIcon(u.role)}
                        <span className="font-medium dark:text-slate-200">{u.name}</span>
                        {u.id === user.id && (
                          <Badge variant="outline" className="text-xs dark:border-slate-600 dark:text-slate-400">Você</Badge>
                        )}
                        {u.visible_for_assignment === false && (
                          <Badge variant="outline" className="text-xs border-orange-300 text-orange-600 dark:border-orange-500 dark:text-orange-400" title="Não aparece na lista de atribuição para usuários comuns">
                            Oculto
                          </Badge>
                        )}
                        {u.human_id && (
                          <Badge variant="outline" className="text-xs border-green-300 text-green-600 dark:border-green-500 dark:text-green-400" title="Vinculado a funcionário">
                            <Link2 className="w-3 h-3 mr-1" />
                            Vinculado
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 dark:text-slate-400">{u.email}</TableCell>
                    <TableCell>
                      <Badge 
                        variant={u.role === 'super_admin' ? 'default' : u.role === 'viewer' ? 'outline' : 'secondary'}
                        className={u.role === 'viewer' ? 'border-amber-300 text-amber-700 dark:border-amber-500 dark:text-amber-400' : ''}
                      >
                        {getRoleLabel(u.role)}
                      </Badge>
                    </TableCell>
                    {isSuperAdmin() && (
                      <TableCell className="dark:text-slate-400">{getCompanyName(u.company_id)}</TableCell>
                    )}
                    <TableCell className="text-slate-500 dark:text-slate-400">
                      {u.last_activity ? (
                        <span className="text-green-600 dark:text-green-400">
                          {new Date(u.last_activity).toLocaleString('pt-BR', { 
                            day: '2-digit', 
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500 text-xs">Nunca</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Botão Vincular Funcionário */}
                        {u.role === 'user' && !u.human_id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setUserToVincular(u);
                              setSelectedFuncionarioId('');
                              setVincularDialogOpen(true);
                            }}
                            title="Vincular a Funcionário (para VT)"
                            className="dark:hover:bg-slate-700"
                          >
                            <Link2 className="w-4 h-4 text-blue-500" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`edit-user-${u.id}`}
                          onClick={() => openPermissionsDialog(u)}
                          title="Editar usuário"
                          className="dark:hover:bg-slate-700"
                        >
                          <Pencil className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`reset-password-${u.id}`}
                          onClick={() => openResetDialog(u.id)}
                          title="Resetar senha"
                        >
                          <Key className="w-4 h-4 text-slate-400" />
                        </Button>
                        {u.id !== user.id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`delete-user-${u.id}`}
                            onClick={(e) => openDeleteDialog(u, e)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resetar Senha</DialogTitle>
            <DialogDescription>
              Defina uma nova senha para o usuário. Informe-a pessoalmente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nova Senha</Label>
              <Input
                id="new-password"
                type="password"
                data-testid="new-password-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              data-testid="reset-password-submit"
              onClick={handleResetPassword}
              disabled={resetting}
            >
              {resetting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Resetar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Permissions Dialog */}
      <Dialog open={permissionsDialogOpen} onOpenChange={setPermissionsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Usuário</DialogTitle>
            <DialogDescription>
              Altere as informações e permissões do usuário
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {/* Nome e Email */}
            <div className="space-y-4 pb-4 border-b">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Nome</Label>
                <Input
                  id="edit-name"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  placeholder="Nome do usuário"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editingEmail}
                  onChange={(e) => setEditingEmail(e.target.value)}
                  placeholder="email@exemplo.com"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Tipo de Acesso</Label>
              <Select
                value={editingRole}
                onValueChange={(value) => handleRoleChange(value, false)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-amber-500" />
                      <span>Visualizador - Só vê tarefas atribuídas</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="user">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-500" />
                      <span>Usuário - Acesso padrão</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="diretoria">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-cyan-500" />
                      <span>Diretoria - Vê departamentos e setores</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <UserCog className="w-4 h-4 text-blue-500" />
                      <span>Admin - Acesso total</span>
                    </div>
                  </SelectItem>
                  {isSuperAdmin() && (
                    <SelectItem value="super_admin">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-blue-500" />
                        <span>Super Admin</span>
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            {editingRole !== 'admin' && editingRole !== 'super_admin' && (
              <div className="space-y-3 pt-2 border-t">
                <Label>Permissões de Acesso</Label>
                <p className="text-xs text-slate-500">Selecione quais módulos este usuário pode acessar</p>
                <div className="space-y-2">
                  {PERMISSION_OPTIONS.map((perm) => (
                    <div 
                      key={perm.id} 
                      className="flex items-center space-x-3 p-2 rounded hover:bg-slate-50"
                    >
                      <Checkbox
                        id={`edit-perm-${perm.id}`}
                        checked={editingPermissions.includes(perm.id)}
                        onCheckedChange={() => togglePermission(perm.id, false)}
                      />
                      <div className="flex-1">
                        <label 
                          htmlFor={`edit-perm-${perm.id}`}
                          className="text-sm font-medium cursor-pointer"
                        >
                          {perm.label}
                        </label>
                        <p className="text-xs text-slate-500">{perm.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {(editingRole === 'admin' || editingRole === 'super_admin') && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                Administradores têm acesso total a todos os módulos do sistema.
              </div>
            )}
            
            {/* Controle de Visibilidade para Atribuição */}
            <div className="space-y-3 pt-4 border-t dark:border-slate-700">
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <div className="flex items-center gap-3">
                  <UserCheck className="w-5 h-5 text-green-500" />
                  <div>
                    <Label className="dark:text-slate-200 font-medium">Visível para atribuição</Label>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Quando ativado, este usuário aparece na lista de atribuição de tarefas para todos
                    </p>
                  </div>
                </div>
                <Switch
                  checked={editingVisibleForAssignment}
                  onCheckedChange={setEditingVisibleForAssignment}
                  data-testid="visible-for-assignment-switch"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermissionsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSavePermissions}
              disabled={savingPermissions}
            >
              {savingPermissions && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Excluir Usuário</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o usuário <strong>{userToDelete?.name}</strong>?
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deleting}
            >
              {deleting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Vincular Funcionário */}
      <Dialog open={vincularDialogOpen} onOpenChange={setVincularDialogOpen}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100 flex items-center gap-2">
              <Link2 className="w-5 h-5 text-blue-500" />
              Vincular Usuário a Funcionário
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              Vincule o usuário <strong>{userToVincular?.name}</strong> a um funcionário cadastrado em Humanas.
              <br />
              <span className="text-xs text-amber-600">Isso permite que o usuário acesse seu próprio extrato de VT.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Selecione o Funcionário</Label>
              <Select
                value={selectedFuncionarioId}
                onValueChange={setSelectedFuncionarioId}
              >
                <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600">
                  <SelectValue placeholder="Escolha um funcionário..." />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700 max-h-60">
                  {funcionarios.length === 0 ? (
                    <div className="p-2 text-sm text-slate-500">Nenhum funcionário cadastrado</div>
                  ) : (
                    funcionarios.map((f) => (
                      <SelectItem 
                        key={f.id} 
                        value={f.id}
                        className="dark:text-slate-200 dark:focus:bg-slate-700"
                      >
                        {f.name} {f.email && <span className="text-xs text-slate-400">({f.email})</span>}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVincularDialogOpen(false)} className="dark:border-slate-600 dark:text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleVincularFuncionario} disabled={vinculando || !selectedFuncionarioId}>
              {vinculando && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Vincular
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
